import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../SEO';
import { musicAlbumSchema } from '../../utils/structuredData';
import { submitToPhp } from '../../lib/phpApi';

const SongsOfSongsLanding = () => {
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8001' : '');
      if (!backendUrl) throw new Error('Subscription service is currently unavailable.');
      const response = await fetch(`${backendUrl}/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          source: 'songs_of_songs',
          list_ids: [2], // IRP-Album-Song-of-Songs
          marketing_emails: marketingConsent
        }),
      });

      if (!response.ok) throw new Error('Subscription service is temporarily unavailable.');
      const result = await response.json();
      if (result.success) {
        setSubmitted(true);
        setEmail('');
      } else {
        setError(result.error || 'Subscription failed.');
      }
    } catch (err) {
      setError(err.message || 'Unable to submit your email.');
    }
  };

  const albumData = musicAlbumSchema({
    title: "Songs of Songs: A Love Letter to the Divine",
    artist: "Divine Reyn",
    image: "/images/music/songs-of-songs.webp",
    fullDescription: "A 16-track journey of worship and devotion, blending English and Spanish in an intimate conversation with the divine. Released August 16, 2025.",
    releaseDate: "2025-08-16",
    tracks: [
      'Amor Mio', 'Cancio de Amor', 'Dijiste No', 'Eli, Eli (English)',
      'Eli, Eli (Spanish)', 'Gracias Por Amarme', 'Love Song', 
      'Mi Corazon Clama A Ti', 'My Heart Cries Out To You', 'My Beloved', 
      'Suelta Y Deja A Dios', 'Tu Eres Santo', 'Upside Down', 
      'Yo Quiero Amarte', 'You Are Holy', 'You Said No'
    ]
  });

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title="Songs of Songs: A Love Letter to the Divine | Divine Reyn | Worship Album Out Now"
        description="16 tracks of worship and devotion blending English and Spanish. Divine Reyn's Songs of Songs: A Love Letter to the Divine - intimate, bilingual worship music. Listen now on Apple Music, YouTube Music, and Amazon Music."
        canonical="/launch/songs-of-songs"
        ogImage="/images/music/songs-of-songs.webp"
        ogType="music.album"
        keywords="Songs of Songs album, Divine Reyn, worship music, bilingual worship, English Spanish worship, love letter to divine, intimate worship, Christian music, Infinite Reyn Productions"
        author="Divine Reyn"
        structuredData={albumData}
      />
      {/* Section 1 - Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background - Purple/gold aesthetic from album art */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-950/50 via-black to-black"></div>
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `url('/images/music/songs-of-songs.webp')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50"></div>

        {/* Content */}
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-6xl md:text-8xl font-light mb-8 leading-none">
            A love letter to the divine.
          </h1>
          
          <p className="text-xl md:text-2xl tracking-wider text-white/80 mb-4">
            16 tracks. Two languages. One conversation with the holy.
          </p>
          
          <p className="text-sm tracking-[0.3em] text-gold mb-12">
            FROM DIVINE REYN / INFINITE REYN PRODUCTIONS / MUSIC
          </p>

          {/* Email Form */}
          {!submitted ? (
            <div className="max-w-md mx-auto mb-6">
              <form onSubmit={handleSubmit} className="mb-4">
                {error && (
                  <p className="text-sm text-rose-300 mb-4">{error}</p>
                )}
                <div className="flex flex-col md:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    className="flex-1 bg-white/10 border border-white/20 px-6 py-4 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors"
                  />
                  <button
                    type="submit"
                    className="bg-gold text-black px-8 py-4 tracking-wider text-sm font-medium hover:bg-gold/90 transition-colors whitespace-nowrap"
                  >
                    GET UPDATES
                  </button>
                </div>
              </form>
              <label className="flex items-start gap-3 cursor-pointer group text-left">
                <div className="relative mt-1">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 border border-white/20 transition-colors ${marketingConsent ? 'bg-gold border-gold' : 'bg-white/5 group-hover:border-gold/50'}`}>
                    {marketingConsent && (
                      <svg className="w-full h-full text-black p-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-[10px] md:text-xs text-white/50 leading-relaxed group-hover:text-white/70 transition-colors">
                  I agree to receive updates and marketing emails from Infinite Reyn Productions. You can unsubscribe at any time.
                </span>
              </label>
            </div>
          ) : (
            <div className="max-w-md mx-auto mb-6 p-6 border border-gold/50 bg-gold/5">
              <p className="text-gold tracking-wider">You're on the list. Watch your inbox.</p>
            </div>
          )}

          <p className="text-xs text-white/40">
            Be the first to know about new releases. No spam.
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-60">
          <div className="w-px h-12 bg-gradient-to-b from-transparent to-white/50"></div>
          <span className="text-xs tracking-widest mr-[-0.1em]">SCROLL</span>
        </div>
      </section>

      {/* Section 2 - The Hook */}
      <section className="py-32 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-12 text-center">
            <div className="pb-8 border-b border-white/10">
              <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed">
                Listen to the full album now on your favorite streaming platform.
              </p>
            </div>
            
            <div className="pb-8 border-b border-white/10">
              <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed">
                Exclusive updates about upcoming music from Divine Reyn.
              </p>
            </div>
            
            <div className="pb-8 border-b border-white/10">
              <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed">
                First look at everything from Infinite Reyn Productions —<br className="hidden md:block" />
                books, music, theatre — before anyone else.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 - The Album */}
      <section className="py-32 px-6 bg-gradient-to-b from-black to-purple-950/20" id="listen">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Album Cover */}
            <div className="relative">
              <img
                src="/images/music/songs-of-songs.webp"
                alt="Songs of Songs: A Love Letter to the Divine by Divine Reyn"
                className="w-full max-w-md mx-auto shadow-2xl"
              />
            </div>

            {/* Album Info */}
            <div>
              <p className="text-3xl md:text-4xl font-light italic mb-6 leading-tight">
                Worship that meets the sacred
              </p>
              <p className="text-3xl md:text-4xl font-light italic mb-12 leading-tight">
                in difficult places.
              </p>

              {/* Genre Tags */}
              <div className="flex flex-wrap gap-4 mb-12 text-xs tracking-[0.3em] text-gold">
                <span>WORSHIP</span>
                <span>·</span>
                <span>BILINGUAL</span>
                <span>·</span>
                <span>INTIMATE</span>
              </div>

              {/* Pull Quote */}
              <blockquote className="border-l-2 border-gold pl-6 mb-12">
                <p className="text-lg text-white/70 font-light italic leading-relaxed mb-4">
                  "16 tracks that bridge languages and spirit. English and Spanish woven together in a tapestry of devotion."
                </p>
              </blockquote>

              {/* Streaming Links */}
              <div className="space-y-4 mb-12">
                <a 
                  href="https://music.apple.com/us/album/songs-of-songs-a-love-letter-to-the-divine/1834690128"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-white/10 border border-white/20 px-6 py-4 text-center hover:border-gold hover:bg-white/5 transition-colors"
                >
                  <span className="tracking-wider text-sm font-medium">LISTEN ON APPLE MUSIC</span>
                </a>
                
                <a 
                  href="https://www.youtube.com/watch?v=E6R62yTvjgE&list=OLAK5uy_mYyk_F5cwWAub_3XS70mYLpoBOy0a5rEc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-white/10 border border-white/20 px-6 py-4 text-center hover:border-gold hover:bg-white/5 transition-colors"
                >
                  <span className="tracking-wider text-sm font-medium">LISTEN ON YOUTUBE MUSIC</span>
                </a>
                
                <a 
                  href="https://www.amazon.com/You-Are-Holy/dp/B0FN48BXMD"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-white/10 border border-white/20 px-6 py-4 text-center hover:border-gold hover:bg-white/5 transition-colors"
                >
                  <span className="tracking-wider text-sm font-medium">LISTEN ON AMAZON MUSIC</span>
                </a>
              </div>

              {/* CTA with Email Form */}
              {!submitted ? (
                <div className="max-w-lg">
                  <form onSubmit={handleSubmit} className="mb-4">
                    <div className="flex flex-col md:flex-row gap-3">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Your email"
                        required
                        className="flex-1 bg-white/10 border border-white/20 px-6 py-4 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors"
                      />
                      <button
                        type="submit"
                        className="bg-gold text-black px-12 py-4 tracking-wider text-sm font-medium hover:bg-gold/90 transition-colors whitespace-nowrap"
                      >
                        STAY UPDATED
                      </button>
                    </div>
                  </form>
                  <label className="flex items-start gap-3 cursor-pointer group text-left">
                    <div className="relative mt-1">
                      <input
                        type="checkbox"
                        checked={marketingConsent}
                        onChange={(e) => setMarketingConsent(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 border border-white/20 transition-colors ${marketingConsent ? 'bg-gold border-gold' : 'bg-white/5 group-hover:border-gold/50'}`}>
                        {marketingConsent && (
                          <svg className="w-full h-full text-black p-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-[10px] md:text-xs text-white/50 leading-relaxed group-hover:text-white/70 transition-colors">
                      I agree to receive updates and marketing emails from Infinite Reyn Productions.
                    </span>
                  </label>
                </div>
              ) : (
                <div className="p-6 border border-gold/50 bg-gold/5">
                  <p className="text-gold tracking-wider">You're on the list.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* Section 4 - Tracklist */}
      <section className="py-24 px-6 bg-black border-t border-white/10" id="tracks">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-xs tracking-[0.3em] text-gold mb-4 uppercase">Tracklist</h2>
            <p className="text-2xl font-light text-white/90">The Complete Journey</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-4">
            {[
              { n: 'Amor Mio', d: '4:12' },
              { n: 'Cancio de Amor', d: '3:39' },
              { n: 'Dijiste No', d: '4:34' },
              { n: 'Eli, Eli (English)', d: '3:49' },
              { n: 'Eli, Eli (Spanish)', d: '3:11' },
              { n: 'Gracias Por Amarme', d: '3:04' },
              { n: 'Love Song', d: '3:30' },
              { n: 'Mi Corazon Clama A Ti', d: '4:10' },
              { n: 'My Heart Cries Out To You', d: '3:47' },
              { n: 'My Beloved', d: '3:58' },
              { n: 'Suelta Y Deja A Dios', d: '5:03' },
              { n: 'Tu Eres Santo', d: '3:37' },
              { n: 'Upside Down', d: '4:43' },
              { n: 'Yo Quiero Amarte', d: '3:19' },
              { n: 'You Are Holy', d: '3:29' },
              { n: 'You Said No', d: '4:14' }
            ].map((track, index) => (
              <div key={index} className="flex items-center justify-between py-3 border-b border-white/5 group hover:border-gold/30 transition-colors">
                <div className="flex items-baseline gap-4">
                  <span className="text-[10px] font-mono text-gold/60">{String(index + 1).padStart(2, '0')}</span>
                  <span className="text-lg font-light text-white/80 group-hover:text-white transition-colors">{track.n}</span>
                </div>
                <span className="text-[10px] font-mono text-white/20">{track.d}</span>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-sm text-white/40 mb-8 max-w-lg mx-auto">
              16 songs. 1 hour 2 minutes. A complete bilingual worship experience produced by Infinite Reyn Productions.
            </p>
          </div>
        </div>
      </section>

      {/* Section 4 - The Imprint */}
      <section className="py-24 px-6 bg-black border-y border-white/10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-px bg-gold mx-auto mb-8"></div>
          
          <p className="text-lg text-white/70 font-light leading-relaxed mb-6">
            <strong className="text-white font-medium">Infinite Reyn Productions</strong> is a creative house built for stories that refuse to stay in one form. Books. Music. Theatre. One source.
          </p>
          
          <p className="text-sm tracking-[0.3em] text-gold">
            WHERE STORIES TAKE FORM
          </p>
        </div>
      </section>

      {/* Section 5 - Final CTA */}
      <section className="py-32 px-6 bg-black">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-8">
            Don't miss what's next.
          </h2>
          
          <p className="text-white/60 mb-12">
            New music, behind-the-scenes, and exclusive updates from Divine Reyn.
          </p>

          {!submitted ? (
            <div className="max-w-md mx-auto">
              <form onSubmit={handleSubmit} className="mb-4">
                <div className="flex flex-col md:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    className="flex-1 bg-white/10 border border-white/20 px-6 py-4 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors"
                  />
                  <button
                    type="submit"
                    className="bg-gold text-black px-8 py-4 tracking-wider text-sm font-medium hover:bg-gold/90 transition-colors whitespace-nowrap"
                  >
                    NOTIFY ME
                  </button>
                </div>
              </form>
              <label className="flex items-start gap-3 cursor-pointer group text-left">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 border border-white/20 transition-colors ${marketingConsent ? 'bg-gold border-gold' : 'bg-white/5 group-hover:border-gold/50'}`}>
                    {marketingConsent && (
                      <svg className="w-full h-full text-black p-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-[10px] text-white/40 leading-tight group-hover:text-white/60 transition-colors">
                  I agree to receive communications from IRP.
                </span>
              </label>
            </div>
          ) : (
            <div className="max-w-md mx-auto p-6 border border-gold/50 bg-gold/5">
              <p className="text-gold tracking-wider">You're on the list. Watch your inbox.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-sm text-white/40">
          <Link to="/" className="hover:text-white transition-colors">
            ← Back to Infinite Reyn Productions
          </Link>
          <p>© 2026 Infinite Reyn Productions</p>
        </div>
      </footer>
    </div>
  );
};

export default SongsOfSongsLanding;
